import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, getMl } from "@/lib/api";
import MultilingualField from "@/components/MultilingualField";
import FileUploader from "@/components/FileUploader";

const empty = () => ({
  title: { en: "", tr: "", id: "" },
  description: { en: "", tr: "", id: "" },
  pdf_url: "", cover_image: "", category: "general", sort_order: 0,
});

const AdminCatalogues = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/catalogues").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onSave = async (data) => {
    try {
      if (editing?.id) await api.put(`/catalogues/${editing.id}`, data);
      else await api.post("/catalogues", data);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Failed"); }
  };

  const onDelete = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/catalogues/${id}`);
    load();
  };

  return (
    <div data-testid="admin-catalogues">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-display text-3xl text-[#0A1128]">Catalogues</h1>
        <button onClick={() => setEditing(empty())} data-testid="add-cat-btn" className="btn-gold"><Plus size={14} /> Add Catalogue</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-slate-200 p-5 rounded-sm" data-testid={`catalogue-row-${c.id}`}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-2">{c.category}</div>
            <h3 className="font-display text-lg mb-1">{getMl(c.title, "en")}</h3>
            <p className="text-xs text-slate-600 line-clamp-2">{getMl(c.description, "en")}</p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditing(c)} data-testid={`edit-catalogue-${c.id}`} className="text-slate-400 hover:text-[#0A1128]"><Pencil size={14} /></button>
              <button onClick={() => onDelete(c.id)} data-testid={`del-catalogue-${c.id}`} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-6 overflow-y-auto" data-testid="catalogue-form">
          <div className="bg-white max-w-2xl w-full rounded-sm my-10">
            <div className="flex justify-between items-center p-6 border-b border-slate-200">
              <h2 className="font-display text-2xl">{editing.id ? "Edit" : "Add"} Catalogue</h2>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-5">
              <MultilingualField label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testId="cat-title" required />
              <MultilingualField label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testId="cat-desc" multiline />
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Category</label>
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="cat-category" className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm">
                  <option value="general">General</option>
                  <option value="company-profile">Company Profile</option>
                  <option value="product">Product Catalogue</option>
                  <option value="commodity">Commodity Catalogue</option>
                  <option value="service">Service Brochure</option>
                </select>
              </div>
              <FileUploader label="PDF File *" accept="application/pdf" value={editing.pdf_url} onChange={(v) => setEditing({ ...editing, pdf_url: v })} testId="cat-pdf" />
              <FileUploader label="Cover Image (optional)" accept="image/*" value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: v })} testId="cat-cover" />
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
              <button onClick={() => setEditing(null)} className="text-xs uppercase tracking-[0.2em] text-slate-500 px-5">Cancel</button>
              <button onClick={() => onSave(editing)} data-testid="cat-save-btn" className="btn-gold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCatalogues;
